import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import MainLayout from "./componments/main-layout";
import IntroGate from "./componments/custom/intro-gate/intro-gate";
import RequireUserAuth from "./componments/guard/require-user-auth";
import { track, EVENTS } from "./utils/meta-pixel";
import HomePageComponent from "./componments/pages/home/home-page";
import AccountPage from "./componments/pages/account/account-page";
import MyPage from "./componments/pages/my-page/my-page";
import MyReportPage from "./componments/pages/my-report/my-report-page";
import CouponPage from "./componments/pages/coupon/coupon-page";
import PersonalEditorPage from "./componments/pages/personal-editor/personal-editor-page";
import FuyaoziAskPage from "./componments/pages/fuyaozi-ask/fuyaozi-ask-page";
import BaiyeQuestionPage from "./componments/pages/baiye-question/baiye-question-page";
import SearchPage from "./componments/pages/search/search-page";
import CategoryPage from "./componments/pages/category/category-page";
import ReviewsPage from "./componments/pages/reviews/reviews-page";
import BaziBaiyePage from "./componments/products/bazi-baiye/bazi-baiye-page";
import BaiyeStoryPage from "./componments/products/bazi-baiye/baiye-story/baiye-story-page";
import ZiweiJingyuePage from "./componments/products/ziwei-jingyue/ziwei-jingyue-page";
import JingyueStoryPage from "./componments/products/ziwei-jingyue/jingyue-story/jingyue-story-page";
import MatchFeihongPage from "./componments/products/match-feihong/match-feihong-page";
import FeihongStoryPage from "./componments/products/match-feihong/feihong-story/feihong-story-page";
import PurchaseResultPage from "./componments/pages/purchase-result/purchase-result-page";

//後台 admin 獨立切成 lazy chunk：一般使用者不進 /admin 就不會下載這包 JS
const AdminApp = lazy(() => import("./componments/admin/admin-app"));

//每次路由變化（pathname 改變）→ 自動捲到頂端
//  解決：所有頁面進入時要置頂，不用每個 page component 自己加 useEffect
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

//—— Meta Pixel 的事件在這裡送 ——
//
//  掛在路由層而不是各頁自己呼叫：故事頁有三支、產品頁有三支，
//  散在六個檔案裡的話，之後加產品一定會漏掉其中一支，
//  而漏掉的那一支在報表上看起來只是「成效比較差」—— 查不出來。
//
//  —— SPA 要自己送 PageView ——
//  Pixel 的 snippet 只在「整頁載入」時送一次。這個站換頁不重新整理，
//  不補這一段的話整站只會有首頁一次 PageView。
//
//  —— 事件對照 ——
//    /product/<x>            ViewContent    看到產品、還沒投入
//    /product/<x>/story      ReachedOffer   答完十二題問診、看到自己的命盤
//  票券那兩支不在這裡（購券是分頁不是路由），在各自的元件裡送。
const ROUTE_EVENTS = [
  [/^\/product\/[a-z-]+\/story$/, EVENTS.REACHED_OFFER],
  [/^\/product\/[a-z-]+$/, EVENTS.VIEW_CONTENT],
];

const PixelTracker = () => {
  const { pathname } = useLocation();

  //  —— 同一個路徑只送一次 ——
  //  StrictMode 下 effect 是「掛載 → cleanup → 再掛載」，不擋的話每個事件
  //  都會送兩次（實測：PageView, PageView）。正式 build 不會雙跑，
  //  但擋住比較保險 —— 任何原因造成的重新掛載都不該重複計算，
  //  而廣告事件灌水會直接讓 CPA 看起來只有一半，是會做錯決策的那種錯。
  //
  //  用 ref 記「上一次送的是哪個路徑」而不是布林值：PageView 本來就該
  //  每次換頁都送一次，記死成「只送一次」會變成整站只有第一頁有數據。
  const sentRef = useRef(null);

  useEffect(() => {
    if (sentRef.current === pathname) return;
    sentRef.current = pathname;

    track(EVENTS.PAGE_VIEW);

    const hit = ROUTE_EVENTS.find(([re]) => re.test(pathname));
    //  帶上產品代號：報表上才分得出是哪一支帶來的，
    //  不然三支產品的 ReachedOffer 會混成一個數字。
    //
    //  content_ids 與 content_name 都送同一個值 —— 網址上的這一段就是
    //  constant/products 的 code，而結帳與 CAPI 也都已經換算成同一套
    //  （payment-service 的 pixelCodeOf、product-plans 的 pixelCode）。
    //  四個事件用同一把尺，漏斗與受眾才接得起來。
    if (hit) {
      const code = pathname.split("/")[2] || "";
      track(hit[1], { content_ids: [code], content_name: code });
    }
  }, [pathname]);

  return null;
};

//需要登入的頁面統一用 RequireUserAuth 包起來。
//  它只處理「直接貼網址進來」的情況 —— 一般點擊路徑在按下去的當下就攔截了
//  （見 context/login-gate）。to 是登入完成後要前往的位置。
function App() {
  //開場動畫只在「一進站就落在首頁」時播：
  //  從綠界導回 /purchase-result、或直接貼次層頁網址的人不該被動畫擋住。
  //  狀態掛在 App（整站只掛載一次）而不是首頁元件裡 —— 所以站內點底部頁籤
  //  回首頁不會重播，只有重新整理或重新進站才會從頭開始。
  const [introDone, setIntroDone] = useState(
    () => window.location.pathname !== "/",
  );

  return (
    <BrowserRouter>
      {/* 疊在整站之上；播動畫的同時首頁已經在背後載好了，播完就直接看到內容 */}
      {!introDone && <IntroGate onFinish={() => setIntroDone(true)} />}

      <ScrollToTop />
      <PixelTracker />
      <Routes>
        {/* 前台：所有頁面共用 MainLayout（nav + 底部頁籤 + 全域 dialog） */}
        <Route path="/" element={<MainLayout />}>
          <Route index element={<HomePageComponent />} />

          {/* 搜尋不需要登入：讓人先看得到有什麼服務，願意買再登入 */}
          <Route path="search" element={<SearchPage />} />

          {/* 分類：頂部分類列的去處。和首頁同一層，共用 NavList */}
          <Route path="category/:key" element={<CategoryPage />} />

          {/* 評價頁：首頁「真實回饋」那區的「看更多」。
              不擋登入 —— 它是買之前拿來說服自己的東西，
              擋在登入後面等於只給已經是顧客的人看。 */}
          <Route path="reviews" element={<ReviewsPage />} />

          {/* 產品頁：一支產品一包（componments/products/<code>），各自掛一條路由。
              不做 /product/:code 通用頁 —— 五位角色的排盤輸入與版型差很多
              （白夜要生辰、緋紅要兩個人、伏爻子是問卦占卜），
              塞進同一個元件只會變成一大坨 switch。
              不需要登入：先讓人看完開場、願意算了再擋登入。 */}
          <Route path="product/bazi-baiye" element={<BaziBaiyePage />} />

          {/* 解命之後的故事頁。掛在產品路由底下而不是另開一條 ——
              它是白夜這支產品的一部分，也順便吃到 /product/* 擋掉底部頁籤的規則。 */}
          <Route path="product/bazi-baiye/story" element={<BaiyeStoryPage />} />

          {/* 鏡月・紫微斗數。報告頁還沒做。 */}
          <Route path="product/ziwei-jingyue" element={<ZiweiJingyuePage />} />

          {/* 故事頁。跟白夜一樣是另一條路由：它是可以捲動的長頁，
              而劇情頁是滿版固定高度，兩種版型塞不進同一個元件。 */}
          <Route
            path="product/ziwei-jingyue/story"
            element={<JingyueStoryPage />}
          />

          {/* 緋紅・八字合婚。故事頁與報告頁還沒做。
              合盤要兩個人的生辰，所以問診比白夜鏡月多一組
              （見 match-feihong-page 的 SCENES）。 */}
          <Route path="product/match-feihong" element={<MatchFeihongPage />} />

          {/* 故事頁。跟白夜鏡月一樣另開一條路由：它是可以捲動的長頁，
              而劇情頁是滿版固定高度，兩種版型塞不進同一個元件。 */}
          <Route
            path="product/match-feihong/story"
            element={<FeihongStoryPage />}
          />

          {/* 付款完由綠界導回這裡（ClientBackURL）。
              不擋登入 —— 報告類商品不需要登入就能買（見後端 P-4b），
              走到這裡的人有可能從頭到尾沒有帳號。擋了的話，
              他會在付完錢的那一刻撞上登入牆，而那是最不該出問題的一步。

              這一頁自己走公開的 P-9 查訂單狀態，只拿得到「付了沒、付多少」——
              orderID 在網址上等於公開資訊，不能用它換到發票統編與問診資料。 */}
          <Route path="purchase-result" element={<PurchaseResultPage />} />

          <Route
            path="account"
            element={
              <RequireUserAuth
                to="/account"
                message="登入後即可查看你的報告、優惠券與個人資料。"
              >
                <AccountPage />
              </RequireUserAuth>
            }
          />

          {/* 報告收藏庫。帳號中心那一列本來就指著 /my-report，
              但這條 route 一直沒做 —— 點下去是白頁。
              要登入：它列的是「這個帳號買過什麼」，沒有身分就沒有清單。 */}
          <Route
            path="my-report"
            element={
              <RequireUserAuth
                to="/my-report"
                message="登入後即可查看你買過的命書。"
              >
                <MyReportPage />
              </RequireUserAuth>
            }
          />

          {/* 優惠券。帳號中心那一列本來就指著 /coupon，但這條 route
              一直沒做 —— 點下去是白頁（同 /my-report、/support）。
              ⚠️ 後端還沒有優惠券模型，所以這一頁目前一定是空狀態。 */}
          <Route
            path="coupon"
            element={
              <RequireUserAuth
                to="/coupon"
                message="登入後即可查看你的優惠券。"
              >
                <CouponPage />
              </RequireUserAuth>
            }
          />

          <Route
            path="my-page"
            element={
              <RequireUserAuth
                to="/my-page"
                message="登入後即可管理你的個人資料與帳號。"
              >
                <MyPage />
              </RequireUserAuth>
            }
          />

          {/* 個人資料編輯。生辰收在帳號上而不是每條動線各收一次 ——
              提問（白夜）要「就著你的命盤問」卻沒有訂單可以依附，
              鏡月與緋紅之後也要同一份（見 models/user 的說明）。

              ⚠️ my-page 早就有兩個連結指著 /personal-editor，但這條 route
              一直不存在，點下去是白頁。2026-10-28 補上。 */}
          <Route
            path="personal-editor"
            element={
              <RequireUserAuth
                to="/personal-editor"
                message="登入後即可填寫你的生辰資料。"
              >
                <PersonalEditorPage />
              </RequireUserAuth>
            }
          />

          {/* 問卦（伏爻子）與提問（白夜）各自一支元件、各自一包。
              原本是同一支吃不同的 persona —— 那時兩邊畫面一模一樣，共用是對的。
              現在問卦長成一整套儀式（歡迎動畫 → 寫問題 → 覆述 → 付款 → 搖龜殼
              → 卜卦），提問仍然只是對話，所以拆開（Kevin 2026-10-07 決定）。

              —— 這兩頁刻意**不包 RequireUserAuth**（Kevin 2026-10-08 決定）——
              擋在門口的話，使用者連那一頁長什麼樣都還沒看到就被要求登入，
              他沒有理由答應。改成讓他先進去、先看到角色，等他真的要打字
              （focus 輸入框）才跳登入 —— 那時候他已經想問了。
              攔截點在各自的 chat 元件；購券那一步也有一道（券記在帳號上）。 */}
          <Route path="ask" element={<FuyaoziAskPage />} />

          <Route path="question" element={<BaiyeQuestionPage />} />
        </Route>

        {/* 後台 admin：與使用者端平行的兄弟路由，不繼承 MainLayout */}
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<div>載入中…</div>}>
              <AdminApp />
            </Suspense>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
